import { useEffect, ReactNode, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

import {
  Header,
  ProfileTaxSituationForm,
  OrderCenterForm,
  Tabs,
} from '@/components'

import { DEFAULT_LIMIT_PAGE } from '@/constants'
import {
  trackEvent,
  useParamsState,
  onConfirmDangerMessage,
  onSuccessMessage,
  onErrorMessage,
} from '@/helpers'
import { useAuth } from '@/providers'
import { ROUTES_ADMIN } from '@/router'

import {
  OrderCenterTable,
  OrderCenterTableTransaction,
  OrderCenterMetrics,
} from '../components'

import {
  useOrderCenterProfileList,
  useOrderCenterFinanceList,
  useOrderCenterLogisticList,
  useOrderCenterSaleOrderList,
  useOrderCenterSaleOrderRemove,
  useTransactionList,
  useUserList,
  useOrderCenterCreditList,
} from '@/services'
import { OrderCenterPageEnum } from './list.type'
import { SaleOrderCreditWithPagesProps, SaleOrderWithPagesProps } from '@/types'

import styles from './list.module.css'

const defaultFilter = {
  page: '1',
  limit: `${DEFAULT_LIMIT_PAGE}`,
  search: '',
  taxStatus: '',
  moscow: '',
  created_at_before: '',
  created_at_after: '',
  ordering: '',
  key_account_manager: '',
}

export const OrderCenterPage = () => {
  const { t } = useTranslation('screenOrderCenterPage')
  const navigate = useNavigate()

  const { whoAmI } = useAuth()
  const { data: dataUserList } = useUserList({
    params: {
      limit: 100,
      page: 1,
      isStaff: 'true',
    },
  })

  const location = useLocation()
  const page = location.pathname.split('/').pop()

  const [filter, setFilter] = useParamsState({
    ...JSON.parse(JSON.stringify(defaultFilter)),
  })

  const [queryParams, setQueryParams] = useParamsState({
    uuid: '',
    action: '',
  })

  const {
    data: dataProfile,
    isLoading: isLoadingProfile,
    refetch: refetchProfile,
  } = useOrderCenterProfileList(filter, page === 'profile')

  const {
    data: dataFinance,
    isLoading: isLoadingFinance,
    refetch: refetchFinance,
  } = useOrderCenterFinanceList(filter, page === 'finance')

  const {
    data: dataCredit,
    isLoading: isLoadingCredit,
    refetch: refetchCredit,
  } = useOrderCenterCreditList(filter, page === 'credit')

  const {
    data: dataLogistic,
    isLoading: isLoadingLogistic,
    refetch: refetchLogistic,
  } = useOrderCenterLogisticList(filter, page === 'logistic')

  const {
    data: dataSaleOrder,
    isLoading: isLoadingSaleOrder,
    refetch: refetchSaleOrder,
  } = useOrderCenterSaleOrderList(filter, page === 'sale' || page === 'all')

  const {
    data: dataTransaction,
    isLoading: isLoadingTransaction,
    refetch: refetchTransaction,
  } = useTransactionList(filter, page === 'transaction')

  const refetch = () => {
    const objectRefetch: Record<string, () => void> = {
      profile: refetchProfile,
      finance: refetchFinance,
      logistic: refetchLogistic,
      sale: refetchSaleOrder,
      transaction: refetchTransaction,
      credit: refetchCredit,
    }

    objectRefetch[page as string]()
  }

  const whoAmIUser = useMemo(() => {
    if (whoAmI?.email) {
      return dataUserList?.data?.results?.find(
        (user) => user.email === whoAmI?.email,
      )
    }
  }, [dataUserList, whoAmI])

  const userFiltered = useMemo(() => {
    if (
      filter.key_account_manager &&
      filter.key_account_manager === 'undefined'
    ) {
      return dataUserList?.data?.results?.find(
        (user) => user.email === whoAmI?.email,
      )
    }

    return dataUserList?.data?.results?.find(
      (user) => user.id === +filter?.key_account_manager,
    )
  }, [dataUserList, filter.key_account_manager, whoAmI])

  const { mutateAsync: mutateDelete } = useOrderCenterSaleOrderRemove()

  const onHandleNavigate = (page: string) => {
    setFilter({
      ...JSON.parse(JSON.stringify(defaultFilter)),
    })

    navigate(ROUTES_ADMIN.ORDER_CENTER.fullPath(page), { replace: true })
  }

  const onHandleDelete = async (uuid: string) => {
    const confirm = await onConfirmDangerMessage(
      t('orderCenterTableDeleteConfirmTitle'),
      t('orderCenterTableDeleteConfirmMessage'),
      t('orderCenterTableDeleteConfirmButtonDelete'),
      t('orderCenterTableDeleteConfirmButtonCancel'),
    )

    if (confirm) {
      const result = await mutateDelete(uuid)

      if (result.success) {
        onSuccessMessage(
          t('orderCenterTableDeleteSuccessTitle'),
          t('orderCenterTableDeleteSuccessMessage'),
        )

        refetch()
      } else {
        onErrorMessage(
          t('orderCenterTableDeleteErrorTitle'),
          t('orderCenterTableDeleteErrorMessage'),
        )
      }
    }
  }

  const defaultProps = {
    filter,
    onEditRegister: (uuid: string) =>
      setQueryParams({
        uuid,
        action: 'form',
      }),
    onSetFilter: setFilter,
    onShowRegister: (uuid: string) =>
      navigate(ROUTES_ADMIN.ORDER_CENTER.ALL.SHOW.fullPath(uuid)),
    formattedFilter: {
      key_account_manager: userFiltered?.email || '',
    },
    onDeleteRegister: onHandleDelete,
  }

  const objectsRoute: Record<string, ReactNode> = {
    all: (
      <OrderCenterTable
        {...defaultProps}
        data={dataSaleOrder?.data}
        isLoading={isLoadingSaleOrder}
        type="all"
      />
    ),
    sale: (
      <OrderCenterTable
        {...defaultProps}
        data={dataSaleOrder?.data}
        isLoading={isLoadingSaleOrder}
        type="sale"
      />
    ),
    profile: (
      <OrderCenterTable
        {...defaultProps}
        data={dataProfile?.data}
        isLoading={isLoadingProfile}
        type="profile"
      />
    ),
    finance: (
      <OrderCenterTable
        {...defaultProps}
        data={dataFinance?.data as SaleOrderWithPagesProps}
        isLoading={isLoadingFinance}
        type="finance"
      />
    ),
    transaction: (
      <OrderCenterTableTransaction
        {...defaultProps}
        data={dataTransaction?.data}
        isLoading={isLoadingTransaction}
      />
    ),
    logistic: (
      <OrderCenterTable
        {...defaultProps}
        data={dataLogistic?.data as SaleOrderWithPagesProps}
        isLoading={isLoadingLogistic}
        type="logistic"
      />
    ),
    credit: (
      <OrderCenterTable
        {...defaultProps}
        data={dataCredit?.data as SaleOrderCreditWithPagesProps}
        isLoading={isLoadingCredit}
        type="credit"
      />
    ),
  }

  useEffect(() => {
    trackEvent('adminOrderCenterPageView', { page })

    if (page === 'sale' && whoAmI?.email) {
      setFilter({
        ...filter,
        page: '1',
        key_account_manager: `${whoAmIUser?.id}`,
      })
    }

    if (page !== 'sale') {
      setFilter({
        ...filter,
        page: '1',
        key_account_manager: '',
      })
    }
  }, [page])

  return (
    <div>
      <Header title={t('title')} description={t('description')} />

      <div className={styles.content}>
        <Tabs
          items={[
            {
              title: t('buttonNavAll'),
              isActive: page === 'all',
              onSelect: () => onHandleNavigate('all'),
            },
            {
              title: t('buttonNavSale'),
              isActive: page === 'sale',
              onSelect: () => onHandleNavigate('sale'),
            },
            {
              title: t('buttonNavProfile'),
              isActive: page === 'profile',
              onSelect: () => onHandleNavigate('profile'),
            },
            {
              title: t('buttonNavFinance'),
              isActive: page === 'finance',
              onSelect: () => onHandleNavigate('finance'),
            },
            {
              title: t('buttonNavTransaction'),
              isActive: page === 'transaction',
              onSelect: () => onHandleNavigate('transaction'),
            },
            {
              title: t('buttonNavLogistic'),
              isActive: page === 'logistic',
              onSelect: () => onHandleNavigate('logistic'),
            },
            {
              title: t('buttonNavCredit'),
              isActive: page === 'credit',
              onSelect: () => onHandleNavigate('credit'),
            },
          ]}
        />

        <OrderCenterMetrics
          type={page as OrderCenterPageEnum}
          filter={filter}
          onFilterChange={setFilter}
        />

        {objectsRoute?.[page as string]}
      </div>

      <ProfileTaxSituationForm
        isOpen={queryParams.action === 'form' && page === 'profile'}
        onHandleOpen={() =>
          setQueryParams({ ...queryParams, action: '', uuid: '' })
        }
        saleOrderUuid={queryParams.uuid}
        onSuccessUpdated={() => refetchProfile()}
      />

      <OrderCenterForm
        isOpen={queryParams.action === 'form' && page !== 'profile'}
        onHandleOpen={() =>
          setQueryParams({ ...queryParams, action: '', uuid: '' })
        }
        saleOrderUuid={queryParams.uuid}
        type={page}
      />
    </div>
  )
}
